import React, { createContext, useContext, useState, useEffect } from 'react'
import { 
  signIn as authSignIn, 
  signUp as authSignUp, 
  signOut as authSignOut,
  getCurrentUser,
  onAuthStateChange 
} from '../services/authService'
import toast from 'react-hot-toast'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)

  // Initialize auth state
  useEffect(() => {
    loadUser()

    // Listen to auth changes
    const { data: authListener } = onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        loadUser()
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        setProfile(null)
        setIsAdmin(false)
      }
    })

    return () => {
      authListener?.subscription?.unsubscribe()
    }
  }, [])

  const loadUser = async () => {
    try {
      setLoading(true)
      const { user: authUser, profile: userProfile, error } = await getCurrentUser()
      
      if (error) throw new Error(error)
      
      if (authUser && userProfile) {
        setUser(authUser)
        setProfile(userProfile)
        setIsAdmin(userProfile.role === 'admin')
      } else {
        setUser(null)
        setProfile(null)
        setIsAdmin(false)
      }
    } catch (error) {
      console.error('Failed to load user:', error)
      setUser(null)
      setProfile(null)
      setIsAdmin(false)
    } finally {
      setLoading(false)
    }
  }

  const signUp = async (userData) => {
    try {
      setLoading(true)
      const { data, error } = await authSignUp(userData)
      
      if (error) {
        toast.error(error)
        return { success: false, error }
      }

      if (data?.user) {
        toast.success('Account created successfully! Please check your email to verify.')
        await loadUser()
        return { success: true, data }
      }

      return { success: false, error: 'Failed to create account' }
    } catch (error) {
      toast.error(error.message)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (credentials) => {
    try {
      setLoading(true)
      const { data, error } = await authSignIn(credentials)
      
      if (error) {
        toast.error(error)
        return { success: false, error }
      }

      if (data?.user) {
        toast.success('Welcome back!')
        await loadUser()
        return { success: true, data }
      }

      return { success: false, error: 'Failed to sign in' }
    } catch (error) {
      toast.error(error.message)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      const { error } = await authSignOut()
      
      if (error) {
        toast.error(error)
        return { success: false, error }
      }

      setUser(null)
      setProfile(null)
      setIsAdmin(false)
      toast.success('Logged out successfully')
      return { success: true }
    } catch (error) {
      toast.error(error.message)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  const refreshUser = async () => {
    await loadUser()
  }

  const value = {
    user,
    profile,
    loading,
    isAdmin,
    isAuthenticated: !!user,
    signUp,
    signIn,
    signOut,
    refreshUser
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext